import { lazy, Suspense, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import type { LessonInput } from '@ready/shared';
import { useApi } from '@/lib/api';
import { Badge, ErrorText } from '@/components/ui';

const LessonBlocks = lazy(() =>
  import('@/components/course/lesson-blocks').then((m) => ({ default: m.LessonBlocks })),
);

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

/**
 * The lesson draft in the editor (saved or not) rendered as learners see it:
 * Markdown, diagrams, math, files and knowledge checks. Answers are marked
 * here from the answer keys; nothing is recorded.
 */
export function LessonPreview({ lessonId, input }: { lessonId: string; input: LessonInput }) {
  const api = useApi();
  const preview = useQuery({
    queryKey: ['lessonPreview', lessonId, JSON.stringify(input)],
    queryFn: () => api.previewLesson(lessonId, input),
    retry: false,
  });
  const keys = preview.data?.answerKeys;
  const check = useCallback(
    async (_blockId: string, questionId: string, optionIds: string[]) => {
      const key = keys?.[questionId];
      const correct = Boolean(key && sameSet(key.correctOptionIds, optionIds));
      return {
        correct,
        explanation: correct ? (key?.explanation ?? null) : null,
        skill: key?.skill ?? '',
      };
    },
    [keys],
  );

  return (
    <div className="space-y-6">
      <p className="flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
        <Eye className="size-4 shrink-0" aria-hidden />
        Preview: this is how learners will see the lesson, including unsaved changes. Answers and
        progress aren't recorded. Answers to questions from Nirvik&apos;s library can&apos;t be
        checked here.
      </p>
      {preview.isPending && <p className="text-sm text-muted-foreground">Loading preview…</p>}
      <ErrorText error={preview.error} />
      {preview.data && (
        <article className="min-w-0 space-y-6">
          <header>
            <h1 className="text-2xl font-semibold">{preview.data.title}</h1>
            {preview.data.summary && (
              <p className="mt-1 text-muted-foreground">{preview.data.summary}</p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {preview.data.optional && <Badge>Optional</Badge>}
              {preview.data.estimatedMinutes && <Badge>{preview.data.estimatedMinutes} min</Badge>}
            </div>
          </header>
          {preview.data.blocks.length === 0 ? (
            <p className="text-sm text-muted-foreground">This lesson has no content yet.</p>
          ) : (
            <Suspense fallback={<p className="text-sm text-muted-foreground">Loading lesson…</p>}>
              <LessonBlocks
                blocks={preview.data.blocks}
                assets={preview.data.assets}
                onCheckAnswer={check}
              />
            </Suspense>
          )}
        </article>
      )}
    </div>
  );
}
